import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { clx } from '@medusajs/ui';

import AppLink, { type AppLinkProps } from '@adapters/app-link';
import { omitKeys, type ClassValue } from '@modules/common/types';
import type { TextVariant } from '@modules/common/components/text';
import { variantStyles } from '@modules/common/components/text/variant-styles';

type TextLinkBaseProps = {
  variant: TextVariant;
  children: ReactNode;
  className?: ClassValue;
};

type TextLinkAsRoute = TextLinkBaseProps &
  Omit<AppLinkProps, keyof TextLinkBaseProps | 'as'> & {
    as?: 'route';
    href: string;
  };

type TextLinkAsExternal = TextLinkBaseProps &
  Omit<ComponentPropsWithoutRef<'a'>, keyof TextLinkBaseProps | 'as'> & {
    as: 'external';
    href: string;
  };

type TextLinkProps = TextLinkAsRoute | TextLinkAsExternal;

/** Keys to strip from props before spreading onto the rendered element. */
const customPropKeys = new Set(['as', 'variant', 'className'] as const);

const TextLink = (props: TextLinkProps) => {
  const { variant, className, children } = props;

  const baseClassName = clx(
    variantStyles[variant],
    'underline-offset-1 hover:underline',
    className
  );

  if (props.as === 'external') {
    const anchorProps = omitKeys(props, customPropKeys) as Omit<
      ComponentPropsWithoutRef<'a'>,
      'as' | 'variant' | 'className'
    >;
    return (
      <a {...anchorProps} className={baseClassName}>
        {children}
      </a>
    );
  }

  const linkProps = omitKeys(props, customPropKeys) as Omit<
    AppLinkProps,
    'as' | 'variant' | 'className'
  >;
  return (
    <AppLink {...linkProps} className={baseClassName}>
      {children}
    </AppLink>
  );
};

export default TextLink;
export type { TextLinkProps };
